// preflop.ts - hero preflop decision types (PF1). The preflop analogue of RecommendedAction
// (facts.ts): the engine resolves hero's combo against the shipped position×stack×table-type charts
// and the exploit-rule table, and emits a labeled preflop intent + a sizing target the user builds
// themselves. Lives in @postflop/schema (like RecommendedAction) so the PF3 boardless preflop
// facts/digest can project it. The INPUT record (PreflopContext) lives in the engine
// (strategy/preflop.ts), mirroring how StrategyContext lives in strategy/context.ts, not here.
import { z } from 'zod';
import { ActionKindSchema } from './action';
import { FiredRuleSchema } from './facts';
import { IcmPressureSchema } from './buckets';
import { PushFoldPositionSchema, PushFoldStackBandSchema, ShoverClassSchema } from './tournament';
/** Hero's preflop INTENT - the poker-semantic label over the mechanical ActionKind. A limp is a call
 *  of the big blind when first-in; open / 3bet / 4bet are all raises; jam is all-in. This is the
 *  "labeling" gap PF0 §A named (limp vs call, open vs 3bet vs 4bet) made explicit. */
export const PreflopIntentSchema = z.enum([
    'fold',
    'limp',
    'call',
    'open',
    'three_bet',
    'four_bet',
    'jam',
]);
/** Which preflop node hero is at, derived from the facing line (mirror of the villain spot roles). */
export const PreflopRoleSchema = z.enum(['open', 'defend', 'vs3bet', 'vs4bet', 'vsjam']);
/** Range-set family selector - which family of the shipped charts hero plays (PF1 fork Q3 / table-type
 *  branch). loose-passive live → live_loose (wider value opens + limp-isolation); tight → live_tight;
 *  standard → baseline. The caller maps the diagnosed table type (read.ts TableType) to one of these;
 *  distinct too from an archetype's own preflop.rangeSetId (that governs villains, not hero). */
export const RangeFamilySchema = z.enum(['baseline', 'live_loose', 'live_tight']);
/** Preflop sizing target - an intent label + a concrete big-blind target the user types themselves
 *  (do-your-own-math holds preflop, North Star §4.2 PF1 amendment). Precise EV of the size is PF2. */
export const PreflopSizingSchema = z.object({
    /** raise-to (open/iso/3bet/4bet) or call amount, in big blinds. */
    toBB: z.number().nonnegative(),
    /** human-readable convention, e.g. '2.5bb open' / 'iso 3bb+1/limper' / '3x 3bet' / 'all-in'. */
    label: z.string(),
});
export const PreflopAlternativeSchema = z.object({
    intent: PreflopIntentSchema,
    note: z.string().optional(),
});
/** PF5 provenance: when the short-stack / tournament push/fold path resolved the decision (rather than
 *  the cash charts), this block records WHICH DT Nash cell answered and the ICM context carried in. Its
 *  presence on a `PreflopRecommendation` is the machine-readable signal that the effective stack routed
 *  to push/fold (`recommendPreflop` engaged it via `tournament/staging.isPushFoldDepth`); absent ⇒ the
 *  cash chart path resolved it. The jam/call membership itself is chipEV-Nash (the authored DT chart is
 *  the ground truth); `icmPressure` is carried context for the coach/grader, and the ICM-EV overfold
 *  adjustment is ST1's `callAllInDecision` (equity-gated), never re-derived here. */
export const PreflopPushFoldProvenanceSchema = z.object({
    /** the DT push/fold chart that answered (e.g. 'pushfold_9max'). */
    chartId: z.string(),
    /** the push/fold position bucket hero's seat mapped to (BB preserved, unlike the cash bucketing). */
    position: PushFoldPositionSchema,
    /** the authored band the live effective stack snapped to (e.g. '12bb'). */
    band: PushFoldStackBandSchema,
    /** combo-% of the Nash range hero was tested against (teaching/inspection; computed, never stored). */
    rangePct: z.number(),
    /** present on a call-off (vsjam): the shover's class that selected the vsSteal/vsEarly range. */
    shoverClass: ShoverClassSchema.optional(),
    /** the ICM pressure carried on the decision context (none/moderate/severe), when supplied. */
    icmPressure: IcmPressureSchema.optional(),
});
/** The engine's hero preflop recommendation. baselineIntent + finalIntent are BOTH kept so the coach
 *  can teach the deviation, mirroring RecommendedAction (facts.ts §6.2). `action` is the mechanical
 *  ActionKind the hand-state machine consumes. */
export const PreflopRecommendationSchema = z.object({
    role: PreflopRoleSchema,
    /** the chart-membership action before any exploit rule fired. */
    baselineIntent: PreflopIntentSchema,
    /** the action after the exploit rules + biases resolved. */
    finalIntent: PreflopIntentSchema,
    /** mechanical action for hand/legal.ts: fold / check / call / raise / jam. */
    action: ActionKindSchema,
    sizing: PreflopSizingSchema.optional(),
    /** the shipped chart key that resolved hero's combo (provenance), or 'none' for an out-of-range fold. */
    chartKey: z.string(),
    /** the effective range-set family id after any widen/tighten shift (e.g. 'live_loose_9max'). */
    family: z.string(),
    /** true iff hero's combo was in the resolved chart region. */
    inChart: z.boolean(),
    /** limp graded as a leak by default (fork Q2): true when finalIntent is 'limp'. */
    isLeak: z.boolean().optional(),
    targetStatement: z.string().optional(),
    firedRules: z.array(FiredRuleSchema),
    alternatives: z.array(PreflopAlternativeSchema),
    /** PF5: present iff the short-stack / tournament push/fold path (not the cash charts) resolved this
     *  decision - the DT Nash cell + ICM context that answered. Absent ⇒ cash chart resolution. */
    pushFold: PreflopPushFoldProvenanceSchema.optional(),
});
//# sourceMappingURL=preflop.js.map